import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ElipsisCell from "../../../../components/TableBox/Cells/EllipsisCell";
import ElipsisText from "../../../../components/ElipsisText";
import Link from "../../../../components/Link";
import Badge from "../../../../components/Badge";
import Button from "../../../../components/Button";
import { Tooltip } from "@mui/material";
import { RowClassName, Modules, RouteParamsTypes, PermissionModulesEnum, PermissionActionsEnum } from "../../../../enums";
import { TransactionStatus } from "../../../../interfaces/transaction.interface"
import { TableContext } from "../../../../contexts/TableContext";
import { AuthContext } from "../../../../contexts/AuthContext";
import { VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import { hasResourseAccess, formatDollarAmount } from "../../../../utils/methods";
import clsx from "clsx";

interface RowProps {
  item: any;
  className?: RowClassName;
  handleRemoveClick: (_id: string, _hasDeleteAccess: boolean) => void;
}

const Row: React.FC<RowProps> = ({ item, className, handleRemoveClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { permissions, isSystemAdmin } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Transaction]: [PermissionActionsEnum.Update] }] });
  const hasDeleteAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Transaction]: [PermissionActionsEnum.Delete] }] });
  const handleViewClick = (id: string) => {
    navigate(`${Modules.transactions}${RouteParamsTypes.view}/${id}`);
  };

  const handleEditClick = (id: string) => {
    navigate(`${Modules.transactions}${RouteParamsTypes.edit}/${id}`);
  };

  const getStatusVariant = (status: TransactionStatus) => {
    if (status === VALIDATION_FIELD_MAPPING.unBilled) return "secondary";
    else if (status === VALIDATION_FIELD_MAPPING.invoice) return "warning"
    else return "info"
  }

  // Deliberately disjoint from getStatusVariant's palette (secondary/warning/info) so the
  // Statement Status badge is never mistaken for the Invoice Status badge in the same row.
  const getStatementStatusVariant = (status: string) => {
    if (status === 'pending') return "primary";
    else if (status === 'settled') return "success";
    else return "danger"; // generated
  }

  const debitCreditNode = (
    <div className="d-flex align-items-center">
      <i className={clsx(`ri-arrow-${item.debit_credit === 'dr' ? 'down' : 'up'}-line ri-16px text-${item.debit_credit === 'dr' ? 'primary' : 'success'} me-1`)}></i>
      <Badge text={item.debit_credit_label} variant={item.debit_credit === 'dr' ? 'primary' : 'success'} />
    </div>
  );

  const amountNode = (
    <div className="d-flex flex-column">
      <div className="fw-semibold d-flex align-items-center gap-2">
        <span>{item.formatted_sub_total_amount ? formatDollarAmount(item.formatted_sub_total_amount) : '-'}</span>
        {!item.reconciled && !!item.reconciled_by_user && (
          <Tooltip
            title={t("transactions.list.pageTexts.table.reviewedBy", { reviewedBy: item.reconciled_by_user?.name || "-" })}
            arrow
            classes={{ tooltip: "tooltip" }}
          >
            <i className="ri-spam-line text-warning ri-20px" />
          </Tooltip>
        )}
      </div>
    </div>
  );

  const totalAmountNode = (
    <div>
      <div className="fw-semibold">
        <span>{item.total_amount ? formatDollarAmount(item.total_amount) : '-'}</span>
      </div>
      {item.chargeable === false && (
        <ElipsisText text="Non chargeable" width="100%" className="fst-italic text-muted" />
      )}
    </div>

  );

  // A transaction is locked once its own status is Settled, or once it is linked to a
  // completed Manager Statement (statement_status flips to Settled - see ManagerStatement's
  // settleLinkedRecords) - either way it can only be viewed here, not edited.
  const isLocked = item.transaction_status === VALIDATION_FIELD_MAPPING.settled
    || item.statement_status === VALIDATION_FIELD_MAPPING.settled;

  // A transaction attached to ANY Manager Statement (statement_status moves off 'pending'
  // as soon as it's selected onto a statement, well before that statement completes) can no
  // longer be deleted, even though it may still be editable here.
  const isLinkedToStatement = !!item.statement_status && item.statement_status !== 'pending';

  const actionsNode = (
    <div className="d-flex align-items-sm-center justify-content-sm-start">
      {
        hasEditAccess && !isLocked ? (
          <Button
            className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
            icon={<i className="ri-edit-box-line ri-20px"></i>}
            onClick={() => handleEditClick(item.id)}
          />
        ) : (
          <Button
            className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
            icon={<i className="ri-eye-line ri-20px"></i>}
            onClick={() => handleViewClick(item.id)}
          />
        )
      }
      <Tooltip
        title={
          <>
            <div>{t('transactions.list.pageTexts.table.lastUpdatedBy')} {item.updater?.name || "-"}</div>
            <div>{t('transactions.list.pageTexts.table.lastUpdated')} {item.updated_at || "-"}</div>
          </>
        }
        arrow
        classes={{
          tooltip: 'tooltip',
        }}
      >
        <i className="ri-timer-line ri-20px"></i>
      </Tooltip>
      {
        (item.transaction_status === VALIDATION_FIELD_MAPPING.unBilled) && !isLocked && !isLinkedToStatement && (
          <Button
            className="btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
            icon={<i className="ri-delete-bin-line ri-20px"></i>}
            onClick={(e) => handleRemoveClick(item.id, hasDeleteAccess)}
          />
        )
      }
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.transactionDate) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <Link
                  text={item.date ?? "-"}
                  href={isLocked ? `${Modules.transactions}${RouteParamsTypes.view}/${item.id}` : `${Modules.transactions}${RouteParamsTypes.edit}/${item.id}`}
                  onClick={() => isLocked ? handleViewClick(item.id) : handleEditClick(item.id)}
                />
              }
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.propertyName) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.expense_category === 'internal_expense' ? (item.manager?.name ?? "-") : (item.property?.name ?? "-")}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.expenseCategory) {
          return (
            <td key={headCell.id}>
              <Badge
                text={item.expense_category_label || "-"}
                variant={item.expense_category === 'internal_expense' ? 'warning' : 'info'}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.invoiceNumber) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.invoice?.invoice_no ?? "-"}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.adjustAgainstClaim) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <div>
                  <div className="d-flex align-items-center gap-2">
                    {item.adjust_against_claim ? (
                      <Badge text={t('common.yes')} variant="success" />
                    ) : (
                      <span>-</span>
                    )}
                    {item.adjust_against_claim && !item.claim_number && (
                      <Tooltip
                        title={t('transactions.list.pageTexts.table.claimNumberMissingTooltip')}
                        arrow
                        classes={{ tooltip: 'tooltip' }}
                      >
                        <i className="ri-spam-line text-warning ri-20px" />
                      </Tooltip>
                    )}
                  </div>
                  {item.adjust_against_claim && !item.claim_settled && (
                    <ElipsisText text="Claim not settled" width="100%" className="fst-italic text-muted" />
                  )}
                </div>
              }
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.debitCredit) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={debitCreditNode}
            />
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id}>
              <Badge
                text={item.transaction_status_label || "-"}
                variant={getStatusVariant(item.transaction_status)}
              />
            </td>
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.statementStatus) {
          return (
            <td key={headCell.id}>
              <Badge text={item.statement_status_label || "-"} variant={getStatementStatusVariant(item.statement_status)} />
            </td>
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.transactionType) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <div className="d-flex align-items-center">
                  <div className="overflow-hidden text-truncate">
                    <ElipsisText
                      text={item.transaction_type?.name || "-"}
                      width="100%"
                    />
                  </div>
                  {item.transaction_details && (
                    <Tooltip
                      title={item.transaction_details}
                      arrow
                      classes={{
                        tooltip: 'tooltip',
                      }}
                    >
                      <i className="ri-sticky-note-line ms-1 cursor-pointer text-primary" />
                    </Tooltip>
                  )}
                </div>
              }
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.amount) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={amountNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.totalAmount) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={totalAmountNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>{actionsNode}</td>
          );
        }

        return (<ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] ?? "-"} />);
      })}
    </tr>
  );
};

export default Row;
